import { useAuth0 } from '@auth0/auth0-react'
import * as coam from '@cimpress-technology/coam-sapidus'
import { locations } from '@cimpress-technology/logistics-configuration-client'
import * as React from 'react'
import { useParams } from 'react-router'
import * as jsonPatch from 'fast-json-patch'
import { v4 } from 'uuid'
import Preloader from '../common/components/Preloader'
import FourOhFourPage from '../common/FourOhFourPage'
import { Location, ReportsCustomData, Report, Network } from '../common/models'
import {
  getLocation,
  updateLocation as updateLogisticsLocation,
  getNetwork,
  updateNetwork,
} from '../common/proxy/locations-store'
import { checkPermission } from '../common/proxy/networks-store'
import { clone } from '../common/helpers/clone'
import { LocationContext, Provider } from './LocationContext'
import LocationPage from './LocationPage'

export default function LocationPageContainer() {
  const { getAccessTokenSilently, user } = useAuth0()
  const { locationId } = useParams<{ locationId: string }>()
  const [currentLocation, setCurrentLocation] = React.useState<Location>()
  const [loading, setLoading] = React.useState(true)
  const [isNetworkAdmin, setIsNetworkAdmin] = React.useState(false)
  const [reports, setReports] = React.useState<Report[]>([])
  const [network, setNetwork] = React.useState<Network>()

  React.useEffect(() => {
    const fetchLocation = async () => {
      setLoading(true)
      const accessToken = await getAccessTokenSilently()
      const sub = user?.sub ?? ''
      const [logisticsLocation, additionalReports] = await Promise.all([
        getLocation(accessToken, sub, locationId),
        locations.getCustomData<ReportsCustomData>(
          accessToken,
          v4(),
          locationId,
          'reports'
        ),
      ])
      const networkRef =
        logisticsLocation &&
        logisticsLocation._links &&
        logisticsLocation._links.network
      const [isEditable, fetchedNetwork] = networkRef
        ? await Promise.all([
            checkPermission(
              accessToken,
              sub,
              networkRef.id,
              coam.models.LogisticsNetworkPermissions.Update
            ),
            getNetwork(accessToken, sub, networkRef.id),
          ])
        : [false, undefined]

      setIsNetworkAdmin(isEditable)
      setCurrentLocation(logisticsLocation)
      setReports(additionalReports?.data.reports ?? [])
      setNetwork(fetchedNetwork)
      setLoading(false)
    }

    fetchLocation()
  }, [getAccessTokenSilently, locationId, user])

  const updateLocation = async (
    change:
      | locations.models.Location
      | ((location: locations.models.Location) => void),
    correlationId?: string
  ): Promise<void> => {
    const corrId = correlationId ?? v4()
    let locationCopy
    if (typeof change === 'function') {
      locationCopy = clone(currentLocation)
      change(locationCopy!)
    } else {
      locationCopy = change
    }

    const patches = jsonPatch
      .compare(currentLocation!, locationCopy!)
      .filter(patch => !patch.path.includes('href'))

    const accessToken = await getAccessTokenSilently()
    const sub = user?.sub ?? ''

    if (patches.length > 0) {
      await updateLogisticsLocation(
        accessToken,
        locationId,
        currentLocation!.etag,
        patches,
        corrId
      )
    }

    await reloadLocation(accessToken, sub)
  }

  const reloadLocation = async (accessToken: string, sub: string) => {
    const location = await getLocation(accessToken, sub, locationId)
    setCurrentLocation(location)
  }

  if (loading) {
    return <Preloader />
  }

  if (!currentLocation) {
    return <FourOhFourPage />
  }

  const linkResourceToLocation = (
    accessToken: string,
    resourceId: string,
    resourceType: coam.models.ResourceTypes
  ) => {
    return locations.linkResourceToLocationGroup(
      accessToken,
      v4(),
      currentLocation.id,
      resourceId,
      resourceType
    )
  }

  const context: LocationContext = {
    logisticsLocation: currentLocation,
    updateLocation,
    loading,
    isNetworkAdmin,
    reports,
    linkResourceToLocation,
    ...(network && {
      network,
      linkResourceToNetwork: (
        accessToken: string,
        resourceId: string,
        resourceType: coam.models.ResourceTypes
      ) => {
        return locations.linkResourceToNetworkGroup(
          accessToken,
          v4(),
          network.apiNetwork.id,
          resourceId,
          resourceType
        )
      },
      updateNetwork: async (
        change:
          | locations.models.LogisticsNetworkWithLinks
          | ((n: locations.models.LogisticsNetworkWithLinks) => void)
      ): Promise<void> => {
        const accessToken = await getAccessTokenSilently()
        const sub = user?.sub ?? ''
        await updateNetwork(accessToken, network.apiNetwork, change)
        await reloadLocation(accessToken, sub)
      },
    }),
  }

  return (
    <Provider value={context}>
      <LocationPage />
    </Provider>
  )
}
